import React, { createContext, useContext, useState, useCallback } from 'react';
import { Home, Activity, Layers, Boxes, Factory, ShoppingCart, Calculator, BookOpen } from 'lucide-react';

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

// Navigation configuration
const navigationConfig = {
  dashboard: { 
    label: '主控台', 
    icon: Home, 
    category: 'dashboard',
    description: '系統總覽與關鍵指標'
  },
  workflows: { 
    label: '業務流程', 
    icon: Activity, 
    category: 'dashboard',
    description: '專業工作流程管理'
  },
  mrp: { 
    label: '物料需求計劃', 
    icon: Layers, 
    category: 'production',
    description: 'MRP規劃與物料管理'
  },
  inventory: { 
    label: '庫存管理', 
    icon: Boxes, 
    category: 'production',
    description: '庫存控制與倉儲管理'
  },
  production: { 
    label: '生產管理', 
    icon: Factory, 
    category: 'production',
    description: '生產排程與控制'
  },
  'purchase-orders': { 
    label: '採購訂單', 
    icon: ShoppingCart, 
    category: 'orders',
    description: '採購管理與供應商'
  },
  'chart-of-accounts': { 
    label: '會計科目表', 
    icon: Calculator, 
    category: 'accounting',
    description: '會計科目管理'
  },
  journal: { 
    label: '日記帳', 
    icon: BookOpen, 
    category: 'accounting',
    description: '會計分錄管理'
  },
};

export const NavigationProvider = ({ children }) => {
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [currentTab, setCurrentTab] = useState('dashboard');
  const [navigationSource, setNavigationSource] = useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);

  // Add navigation entry to history
  const addToHistory = useCallback((tab, source = null, context = {}) => {
    const entry = {
      tab,
      source,
      context,
      timestamp: new Date().toISOString(),
      config: navigationConfig[tab] || { label: tab, icon: Home }
    };

    setNavigationHistory(prev => {
      const newHistory = [...prev, entry];
      // Keep only last 10 entries to prevent memory issues
      return newHistory.slice(-10);
    });
  }, []);

  // Navigate to a tab with context
  const navigateTo = useCallback((tab, source = null, context = {}) => {
    const previousTab = currentTab;
    
    // Add current tab to history before navigating
    if (previousTab !== tab) {
      addToHistory(previousTab, source, context);
    }
    
    setCurrentTab(tab);
    setNavigationSource(source);
    
    // Update breadcrumb path
    updateBreadcrumbPath(tab, source, context);
  }, [currentTab, addToHistory]);

  // Go back to previous location
  const goBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const lastEntry = navigationHistory[navigationHistory.length - 1];
      setCurrentTab(lastEntry.tab);
      setNavigationSource(lastEntry.source);
      
      // Remove the last entry from history
      setNavigationHistory(prev => prev.slice(0, -1));
      
      // Update breadcrumb
      updateBreadcrumbPath(lastEntry.tab, lastEntry.source, lastEntry.context);
    }
  }, [navigationHistory]);

  // Update breadcrumb path based on current navigation
  const updateBreadcrumbPath = useCallback((tab, source, context = {}) => {
    const path = [
      { label: '主控台', href: 'dashboard', icon: Home }
    ];

    // Add source if coming from workflows
    if (source === 'workflows' && tab !== 'workflows') {
      path.push({ 
        label: '業務流程', 
        href: 'workflows', 
        icon: Activity 
      });
    }

    // Add current tab if not dashboard
    if (tab !== 'dashboard') {
      const config = navigationConfig[tab];
      if (config) {
        path.push({ 
          label: config.label, 
          href: tab, 
          icon: config.icon 
        });
      }
    }

    // Add sub-context if available
    if (context.subView) {
      path.push({ 
        label: context.subView.label || context.subView, 
        href: null, 
        icon: null 
      });
    }

    setBreadcrumbPath(path);
  }, []);

  // Get navigation context for current tab
  const getNavigationContext = useCallback(() => {
    return {
      currentTab,
      navigationSource,
      canGoBack: navigationHistory.length > 0,
      lastVisited: navigationHistory[navigationHistory.length - 1] || null,
      breadcrumbPath,
      config: navigationConfig[currentTab] || { label: currentTab, icon: Home }
    };
  }, [currentTab, navigationSource, navigationHistory, breadcrumbPath]);

  // Check if came from specific source
  const cameFrom = useCallback((source) => {
    return navigationSource === source || 
           navigationHistory.some(entry => entry.source === source);
  }, [navigationSource, navigationHistory]);

  // Get return path to specific source
  const getReturnPath = useCallback((targetSource) => {
    // Find the most recent entry with the target source
    for (let i = navigationHistory.length - 1; i >= 0; i--) {
      const entry = navigationHistory[i];
      if (entry.source === targetSource || entry.tab === targetSource) {
        return {
          tab: entry.tab,
          context: entry.context,
          config: entry.config
        };
      }
    }
    
    // Default return path
    return {
      tab: targetSource,
      context: {},
      config: navigationConfig[targetSource] || { label: targetSource, icon: Home }
    };
  }, [navigationHistory]);

  const value = {
    // State
    currentTab,
    navigationSource,
    navigationHistory,
    breadcrumbPath,
    navigationConfig,
    
    // Actions
    navigateTo,
    goBack,
    addToHistory,
    updateBreadcrumbPath,
    
    // Utilities
    getNavigationContext,
    cameFrom,
    getReturnPath
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationContext; 